import React, { useState, useEffect, useRef } from 'react';
import { Text, Flexbox, DarkTooltip } from '@morressier/ts';
import { IProfile } from 'typings/profile';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSquarePen,
  faLink,
  faGlobe
} from '@fortawesome/pro-regular-svg-icons';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import { faEyeSlash, faEye } from '@fortawesome/pro-light-svg-icons';
import { FaTwitter, FaLinkedin, FaOrcid } from 'react-icons/fa';
import { SkeletonLoader } from 'containers/Search/Submissions';
import omit from 'lodash.omit';
import { useProfile, useResearch } from 'clients/internal/profileService';

import {
  ProfileContainer,
  LinksContainer,
  SecondaryText,
  Textmt8,
  LocationIcon,
  EditButton,
  DetailsContainer,
  DetailsSectionContainer,
  PrimaryAffiliation,
  ProfileBadge,
  UserProfilePage,
  ProfileBadgeLink,
  ProfileBadgeContainer
} from './styles';
import { Avatar, LinkButton } from 'components/ProfileUIComponents';
import ProfileFormModal from './ProfileFormModal';
import { countriesByAlpha2 } from './countriesList';
import MissingProfileIndicator from './MissingProfileIndicator';
import Affiliations from './Affiliations';
import Biography from './Biography';
import Research from './Research';
import CareerStage from './CareerStage';
import Navigation from './Navigation';
import { HonorificsList } from 'utils/constants';
import { careerStageMapping, defaultNavigationLinks } from './constants';
import EmptyState from './EmptyState';
import { useAffiliations } from './Affiliations/hooks';
import { logEvent } from 'utils/eventTracking';

type IUserProfileProps = {
  userId: string;
  loggedInUser: string | null | undefined;
};
const updateProfileText =
  'Add your honorific title, location, and important links to your profile to better introduce yourself to your global research community.';

const UserProfile = (props: IUserProfileProps) => {
  const { userId, loggedInUser } = props;
  // if logged in user and profile visitor are the same
  const isSameUser = loggedInUser === userId;

  const [navigationLinks, setNavigationLinks] = useState(
    defaultNavigationLinks
  );
  const { error, data: profile, mutate, isLoading } = useProfile(userId);

  const {
    data: research = [],
    mutate: mutateResearch,
    isLoading: isResearchLoading
  } = useResearch(userId);

  const {
    data: affiliations = [],
    isLoading: isAffiliationLoading,
    mutate: mutateAffiliationList
  } = useAffiliations(userId);

  const [showEditForm, setShowEditForm] = useState(false);

  const [selectedNavigation, setSelectedNavigation] =
    useState<string>('details');

  useEffect(() => {
    if (!profile) return;

    const handleScroll = () => {
      const elements = [...navigationLinks].reverse().map(({ id }) => ({
        id,
        element: document.getElementById(id)
      }));
      const scrollTop = document.documentElement.scrollTop;
      const selected = elements.find(({ element }) => {
        if (element) {
          const top = element.getBoundingClientRect().top + window.scrollY - 80;
          return scrollTop >= top;
        }
      });
      if (selected?.id && selectedNavigation !== selected?.id) {
        setSelectedNavigation(selected?.id);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [profile, selectedNavigation]);

  useEffect(() => {
    // Track if no Affiliation in the affiliations response and not in a loading state
    if (affiliations.length === 0 && isSameUser && !isAffiliationLoading)
      logEvent('PROFILE_USER_NOT_AFFILIATED');
    // if profile is empty and not same user set to empty array
    if (isEmptyProfile && !isSameUser) {
      setNavigationLinks([]);
    } else {
      const excludedNavigationLinks: string[] = [];
      if (!profile?.careerStage) excludedNavigationLinks.push('career-stage');
      if (!profile?.biography) excludedNavigationLinks.push('biography');
      if (!affiliations?.length) excludedNavigationLinks.push('affiliations');
      if (!research?.length) excludedNavigationLinks.push('research');

      // Exclude navigation for profile visitor if the section detail does not exist
      if (!isSameUser) {
        const updatedNavigationLinks = defaultNavigationLinks.filter(
          ({ id }) => !excludedNavigationLinks.includes(id)
        );
        setNavigationLinks(updatedNavigationLinks);
      }
      if (isSameUser) setNavigationLinks(defaultNavigationLinks);
    }
  }, [
    profile?.biography,
    profile?.careerStage,
    research?.length,
    affiliations?.length,
    isLoading
  ]);

  const {
    firstName,
    lastName,
    title = '',
    pronouns,
    headline,
    countryAlpha2 = '',
    profilePictureURL,
    twitter,
    linkedin,
    websiteLinks,
    primaryAffiliation,
    orcid,
    isOrcidLink,
    careerStage,
    publicProfile,
    biography
  } = profile || {};

  const honorifics = HonorificsList[title];
  const fullName = `${firstName} ${lastName}`;
  const honorificAndFullName = honorifics
    ? `${honorifics} ${fullName}`
    : fullName;

  const openEditForm = () => setShowEditForm(true);
  const closeEditForm = () => setShowEditForm(false);

  const showMissingProfileIndicator =
    !countryAlpha2 &&
    !headline &&
    !linkedin &&
    !pronouns &&
    !title &&
    !twitter &&
    !orcid &&
    websiteLinks?.length == 0 &&
    isSameUser;

  const isEmptyProfile =
    !isResearchLoading &&
    !isAffiliationLoading &&
    !isLoading &&
    !affiliations.length &&
    !research.length &&
    !profile?.biography &&
    !profile?.careerStage;

  if (isLoading || isAffiliationLoading || isResearchLoading) {
    return (
      <>
        <SkeletonLoader />
        <SkeletonLoader />
        <SkeletonLoader />
      </>
    );
  }

  if (error || !profile) return <p>No profile data</p>;

  const publicProfileBadgeText =
    'Public Profile. Your Profile is visible to everybody.';
  const privateProfileBadgeText =
    'Private Profile. Your profile is only visible to signed-in users.';
  const hasPronoun = pronouns && pronouns !== 'no-preference';

  const handleClickLinkedInLink = () => () => () => () => {
    logEvent(
      isSameUser
        ? 'PROFILE_GENERAL_INFORMATION_OPEN_OWN_LINKEDIN_PROFILE'
        : 'PROFILE_GENERAL_INFORMATION_OPEN_OTHER_USER_LINKEDIN_PROFILE',
      isSameUser ? { profile_user: userId } : {}
    );
  };

  const handleClickTwitterLink = () => () => () => {
    logEvent(
      isSameUser
        ? 'PROFILE_GENERAL_INFORMATION_OPEN_OWN_TWITTER_PROFILE'
        : 'PROFILE_GENERAL_INFORMATION_OPEN_OTHER_USER_TWITTER_PROFILE',
      isSameUser ? { profile_user: userId } : {}
    );
  };

  const handleClickOrcidLink = () => () => {
    logEvent(
      isSameUser
        ? 'PROFILE_GENERAL_INFORMATION_OPEN_OWN_ORCID_PROFILE'
        : 'PROFILE_GENERAL_INFORMATION_OPEN_OTHER_USER_ORCID_PROFILE',
      isSameUser ? { profile_user: userId } : {}
    );
  };

  const handleClickWebsite = (url: string) => () => {
    logEvent(
      isSameUser
        ? 'PROFILE_GENERAL_INFORMATION_OPEN_OWN_WEBSITE'
        : 'PROFILE_GENERAL_INFORMATION_OPEN_OTHER_USER_WEBSITE',
      isSameUser ? { profile_user: userId, url } : { url }
    );
  };

  /*
   * if logged in Morressier User
   * or Profile Owner
   * or Public Profile
   * Show Public View of the General Profile Info
   */
  const showPublicProfile = isSameUser || loggedInUser || publicProfile;
  const renderProfileBadge = () => {
    return (
      <ProfileBadgeContainer
        justifyContent="center"
        alignItems="center"
        flexDirection="row"
        gap={6}
      >
        <FontAwesomeIcon icon={publicProfile ? faEye : faEyeSlash} />
        <Text>
          {publicProfile ? publicProfileBadgeText : privateProfileBadgeText}
        </Text>
        <ProfileBadgeLink as="a" href={`${origin}/settings`}>
          Edit Privacy setting
        </ProfileBadgeLink>
      </ProfileBadgeContainer>
    );
  };
  const renderPublicProfile = () => {
    return (
      <Flexbox gap="2rem">
        <Avatar src={profilePictureURL} avatarSize="large" />
        <div>
          <Flexbox gap="0.3rem" alignItems="center">
            <Text size="h4_new" fontWeight="semiBold">
              {honorificAndFullName}
            </Text>
            <DarkTooltip
              text="Verified ORCID profile available"
              placement="bottom"
              width="15rem"
            >
              {isOrcidLink && (
                <FaOrcid color="var(--color-green-30)" size="1.25rem" />
              )}
            </DarkTooltip>
          </Flexbox>
          {showMissingProfileIndicator ? (
            <MissingProfileIndicator
              handleAction={openEditForm}
              title="Update your profile"
              actiontitle="Update profile"
              description={updateProfileText}
            />
          ) : (
            <>
              <Flexbox gap="8px">
                {careerStage && (
                  <SecondaryText fontWeight="medium">
                    {careerStageMapping[careerStage].title}
                  </SecondaryText>
                )}
                {careerStage && countryAlpha2 && <span>.</span>}
                {countryAlpha2 && (
                  <Flexbox alignItems="center">
                    <LocationIcon icon={faGlobe} />
                    <SecondaryText fontWeight="medium">
                      {countriesByAlpha2[countryAlpha2]?.name}
                    </SecondaryText>
                  </Flexbox>
                )}
                {hasPronoun && countryAlpha2 && <span>.</span>}
                {hasPronoun && (
                  <SecondaryText fontWeight="medium">{pronouns}</SecondaryText>
                )}
              </Flexbox>
              {primaryAffiliation?.role &&
                primaryAffiliation?.organizationName && (
                  <PrimaryAffiliation gap="8px">
                    {primaryAffiliation?.role && (
                      <Text>{primaryAffiliation?.role}</Text>
                    )}
                    {primaryAffiliation?.role &&
                      primaryAffiliation?.organizationName && <span>.</span>}
                    {primaryAffiliation?.organizationName && (
                      <Flexbox gap="4px" alignItems="center">
                        <Text>{primaryAffiliation?.organizationName}</Text>
                        {primaryAffiliation?.isVerified && (
                          <FontAwesomeIcon
                            icon={faBadgeCheck}
                            color="var(--color-green-30)"
                          />
                        )}
                      </Flexbox>
                    )}
                  </PrimaryAffiliation>
                )}
              <Textmt8 size="body1_new">{headline}</Textmt8>
              <LinksContainer gap="1rem">
                {isOrcidLink && (
                  <LinkButton
                    as="a"
                    href={orcid}
                    target="_blank"
                    rel="noopener noreferrer"
                    isDisable={!orcid?.length}
                    onClick={handleClickOrcidLink}
                  >
                    <FaOrcid />
                    ORCID
                  </LinkButton>
                )}
                {Boolean(linkedin) && (
                  <LinkButton
                    as="a"
                    href={linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleClickLinkedInLink}
                  >
                    <FaLinkedin />
                    Linkedin
                  </LinkButton>
                )}
                {twitter && (
                  <LinkButton
                    as="a"
                    href={twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleClickTwitterLink}
                  >
                    <FaTwitter />
                    {`@${twitter.split('/').pop()}`}
                  </LinkButton>
                )}
                {websiteLinks?.map(({ title, url }) => (
                  <LinkButton
                    as="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleClickWebsite(url)}
                  >
                    <FontAwesomeIcon icon={faLink} />
                    {title}
                  </LinkButton>
                ))}
              </LinksContainer>
              {isSameUser && (
                <EditButton
                  onClick={() => {
                    logEvent(
                      'PROFILE_GENERAL_INFORMATION_EDIT_PROFILE_CLICKED'
                    );
                    openEditForm();
                  }}
                >
                  <FontAwesomeIcon icon={faSquarePen} />
                  Edit
                </EditButton>
              )}
            </>
          )}
          <ProfileFormModal
            isOpen={showEditForm}
            onDismiss={closeEditForm}
            mutateProfile={mutate}
            data={
              omit(profile, [
                '_id',
                'createdAt',
                'userId',
                'updatedAt'
              ]) as IProfile
            }
          />
        </div>
      </Flexbox>
    );
  };
  const renderPrivateProfile = () => {
    return (
      <Flexbox gap="2rem">
        <Avatar avatarSize="large" />
        <div>
          <Flexbox gap="0.3rem" flexDirection="column">
            <Text size="h4_new" fontWeight="semiBold">
              {fullName}
            </Text>
            <SecondaryText fontWeight="medium">
              This user has a private profile.
            </SecondaryText>
          </Flexbox>
        </div>
      </Flexbox>
    );
  };
  return (
    <UserProfilePage id="profile-container">
      {isSameUser && (
        <ProfileBadge isPublic={publicProfile}>
          {renderProfileBadge()}
        </ProfileBadge>
      )}
      <ProfileContainer id="details">
        {showPublicProfile ? renderPublicProfile() : renderPrivateProfile()}
      </ProfileContainer>
      {/*
       * Don't show the detail section
       * if Detail Section empty and the user is visitor and profile is not public
       */}
      {(!isEmptyProfile || isSameUser) && (loggedInUser || publicProfile) && (
        <DetailsSectionContainer>
          <Navigation
            selected={selectedNavigation}
            setSelectedNavigation={setSelectedNavigation}
            navigationLinks={navigationLinks}
          />
          <DetailsContainer flexDirection="column" gap="2rem">
            {(careerStage || isSameUser) && (
              <CareerStage
                careerStage={careerStage}
                userId={userId}
                isSameUser={isSameUser}
                mutateProfile={mutate}
              />
            )}
            {(affiliations?.length || isSameUser) && (
              <Affiliations
                data={affiliations}
                mutate={mutateAffiliationList}
                isLoading={isAffiliationLoading}
                isSameUser={isSameUser}
                userId={userId}
                affiliationId={primaryAffiliation?.affiliationId}
                mutateProfile={mutate}
              />
            )}
            {(biography || isSameUser) && (
              <Biography
                isSameUser={isSameUser}
                userId={userId}
                profile={profile}
                mutateProfile={mutate}
              />
            )}
            {(research?.length || isSameUser) && (
              <Research
                research={research}
                mutate={mutateResearch}
                isLoading={isResearchLoading}
                isSameUser={isSameUser}
                userId={userId}
              />
            )}
          </DetailsContainer>
        </DetailsSectionContainer>
      )}
      {/*
       * If Profile is empty
       * and user in visitor *
       * and public profile or Morressier logged in user
       * Show the Empty State
       */}
      {isEmptyProfile && !isSameUser && (publicProfile || loggedInUser) && (
        <EmptyState />
      )}
    </UserProfilePage>
  );
};

export default UserProfile;
